import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WorkflowService, WorkflowTestRun } from "../../services/WorkflowService";
import { usePermissions } from "../../PermissionsProvider";
import moment from "moment";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  AlertCircleIcon,
  LoaderIcon,
  MoreHorizontalIcon,
  GitCompareArrows,
} from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { cn } from "../../lib/utils";
import { notification } from "antd";
import SmartPagination from "../shared/SmartPagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { WORKFLOW_TEST_RUN_STORAGE_KEY } from "../TestRun/WorkflowTestRunNew";

interface Props {
  testSetId: number;
  isCompareMode?: boolean;
  selectedTestRunIds?: number[];
  selectedTestRunsMap?: Record<number, boolean>;
  onCompareClick?: () => void;
  onCancelCompare?: () => void;
  onCompareRuns?: () => void;
  onTestRunSelect?: (runId: number, checked: boolean) => void;
}

const RUN_PAGE_SIZE = 5;

const WorkflowTestRunsTab: React.FC<Props> = ({
  testSetId,
  isCompareMode = false,
  selectedTestRunIds = [],
  selectedTestRunsMap = {},
  onCompareClick,
  onCancelCompare,
  onCompareRuns,
  onTestRunSelect,
}) => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();

  const [testRuns, setTestRuns] = useState<WorkflowTestRun[]>([]);
  const [totalTestRuns, setTotalTestRuns] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<Record<number, any>>({});

  const fetchTestRuns = (p: number) => {
    setIsLoading(true);
    const offset = (p - 1) * RUN_PAGE_SIZE;
    WorkflowService.getWorkflowTestRuns(testSetId, offset, RUN_PAGE_SIZE, setIsLoading, apiKey).subscribe(
      (data: { items: WorkflowTestRun[]; total: number }) => {
        setTestRuns(data.items || []);
        setTotalTestRuns(data.total || 0);
        setIsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
      }
    );
  };

  useEffect(() => {
    fetchTestRuns(page);
  }, [testSetId, page, apiKey]);

  useEffect(() => {
    const hasIncomplete = testRuns.some((r) => r.status === "RUNNING" || r.status === "CREATED");
    if (!hasIncomplete) return;

    const poll = () => {
      for (const run of testRuns) {
        if (run.status === "RUNNING" || run.status === "CREATED") {
          WorkflowService.getWorkflowTestRunProgress(run.id, () => {}, apiKey).subscribe(
            (data: any) => setProgress((prev) => ({ ...prev, [run.id]: data })),
            () => {}
          );
        }
      }
    };
    poll();
    const interval = setInterval(() => {
      poll();
      fetchTestRuns(page);
    }, 60000);
    return () => clearInterval(interval);
  }, [testRuns, apiKey, page]);

  const handleDelete = (run: WorkflowTestRun) => {
    WorkflowService.deleteWorkflowTestRun(run.id, () => {}, apiKey).subscribe(
      () => {
        notification.success({ message: "Test run deleted" });
        fetchTestRuns(page);
      },
      (error: any) => {
        notification.error({ message: "Error", description: `Failed to delete: ${error}` });
      }
    );
  };

  const getProgress = (run: WorkflowTestRun) => {
    const p = progress[run.id];
    if (p) return p;
    const done = run.completed_count + run.error_count;
    return {
      total: run.item_count,
      done,
      passed: run.passed_count,
      failed: run.failed_count,
      errored: run.error_count,
      percent: run.item_count > 0 ? Math.round((done / run.item_count) * 100) : 0,
    };
  };

  const newRunButton = (
    <Button
      onClick={() => {
        localStorage.removeItem(WORKFLOW_TEST_RUN_STORAGE_KEY);
        navigate(`/test/workflow-test-sets/${testSetId}/runs/new`);
      }}
      className="gap-2"
    >
      <PlusIcon className="w-4 h-4" />
      New Test Run
    </Button>
  );

  const isRunClickable = (run: WorkflowTestRun) => {
    if (isCompareMode) return false;
    return run.status !== "RUNNING" && run.status !== "CREATED";
  };

  if (isLoading && testRuns.length === 0) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center gap-4 py-4">
            <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
            <div className="h-4 bg-muted rounded w-24 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (testRuns.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-gc-med text-foreground">Test Runs</h3>
            <p className="text-sm text-muted-foreground">
              Manage and compare test run results
            </p>
          </div>
          {newRunButton}
        </div>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="space-y-3">
            <h3 className="text-lg font-gc-med text-foreground">No test runs found</h3>
            <p className="text-muted-foreground max-w-md">
              Create a new test run to get started.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Test Runs</h3>
          <p className="text-sm text-muted-foreground">
            Manage and compare test run results
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!isCompareMode ? (
            <>
              {onCompareClick && (
                <Button variant="outline" onClick={onCompareClick} className="gap-2">
                  <GitCompareArrows className="w-4 h-4" />
                  Compare Runs
                </Button>
              )}
              {newRunButton}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onCancelCompare}>
                Cancel
              </Button>
              <Button
                onClick={onCompareRuns}
                disabled={selectedTestRunIds.length < 2}
                className="gap-2"
              >
                Compare {selectedTestRunIds.length > 0 ? `(${selectedTestRunIds.length})` : ""}
              </Button>
            </>
          )}
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="border-b border-border hover:bg-transparent">
            {isCompareMode && (
              <TableHead className="w-[5%] text-center">Select</TableHead>
            )}
            <TableHead className="w-[12%] text-left">Date</TableHead>
            <TableHead className="w-[18%] text-left">Description</TableHead>
            <TableHead className="w-[12%] text-left">Workflow</TableHead>
            <TableHead className="w-[9%] text-center">Status</TableHead>
            <TableHead className="w-[16%] text-left">Progress</TableHead>
            <TableHead className="w-[9%] text-center">Pass Rate</TableHead>
            <TableHead className="w-[9%] text-center">Avg Time</TableHead>
            <TableHead className="w-[8%] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {testRuns.map((run) => {
            const p = getProgress(run);
            const passRate = p.total > 0 && (p.passed + p.failed) > 0
              ? Math.round((p.passed / (p.passed + p.failed)) * 100)
              : null;
            const isRunning = run.status === "RUNNING" || run.status === "CREATED";
            const clickable = isRunClickable(run);
            return (
              <TableRow
                key={run.id}
                className={cn(
                  "border-b border-border/50 transition-colors",
                  clickable && "cursor-pointer hover:bg-muted/50"
                )}
                onClick={() => clickable && navigate(`/test/workflow-test-sets/${testSetId}/runs/${run.id}`)}
              >
                {isCompareMode && (
                  <TableCell className="py-4 text-center" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedTestRunsMap[run.id] || false}
                      disabled={isRunning}
                      onCheckedChange={(checked) => onTestRunSelect?.(run.id, !!checked)}
                    />
                  </TableCell>
                )}
                <TableCell className="py-4 text-left">
                  <div className="space-y-1">
                    <div className="text-sm font-gc-reg text-foreground">
                      {run.date_created ? moment(run.date_created).format("MMM D, YYYY") : "-"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {run.date_created ? moment(run.date_created).format("h:mm A") : ""}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="py-4 text-left">
                  <span className="text-sm text-foreground line-clamp-2">
                    {run.description || "-"}
                  </span>
                </TableCell>
                <TableCell className="py-4 text-left">
                  <span className="text-sm text-foreground">{run.workflow_name || "-"}</span>
                </TableCell>
                <TableCell className="py-4 text-center">
                  <div className={cn(
                    "inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium",
                    run.status === "COMPLETED" && "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
                    run.status === "ERROR" && "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
                    run.status === "RUNNING" && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
                    run.status === "CREATED" && "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
                  )}>
                    {isRunning && <LoaderIcon className="w-3 h-3 animate-spin" />}
                    {run.status}
                  </div>
                </TableCell>
                <TableCell className="py-4 text-left">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                        <CheckCircleIcon className="w-3 h-3" /> {p.passed}
                      </span>
                      <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                        <XCircleIcon className="w-3 h-3" /> {p.failed}
                      </span>
                      {p.errored > 0 && (
                        <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                          <AlertCircleIcon className="w-3 h-3" /> {p.errored}
                        </span>
                      )}
                      <span className="text-muted-foreground">{p.done}/{p.total}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className="bg-maitai-green h-1.5 rounded-full transition-all"
                        style={{ width: `${p.percent}%` }}
                      />
                    </div>
                  </div>
                </TableCell>
                <TableCell className="py-4 text-center">
                  {passRate !== null ? (
                    <Badge className={cn(
                      "font-gc-reg",
                      passRate >= 80 && "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
                      passRate >= 60 && passRate < 80 && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
                      passRate < 60 && "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                    )}>
                      {passRate}%
                    </Badge>
                  ) : (
                    <span className="text-sm text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="py-4 text-center">
                  <span className="text-sm text-muted-foreground">
                    {run.duration_ms?.p50 != null ? `${run.duration_ms.p50}ms` : "-"}
                  </span>
                </TableCell>
                <TableCell className="py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontalIcon className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-600"
                            onSelect={(e) => e.preventDefault()}
                          >
                            <TrashIcon className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Test Run</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this test run? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(run)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {Math.ceil(totalTestRuns / RUN_PAGE_SIZE) > 1 && (
        <SmartPagination
          currentPage={page}
          totalPages={Math.ceil(totalTestRuns / RUN_PAGE_SIZE)}
          onPageChange={setPage}
          itemName="test runs"
          totalItems={totalTestRuns}
          itemsPerPage={RUN_PAGE_SIZE}
        />
      )}

    </div>
  );
};

export default WorkflowTestRunsTab;
